import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { TotemStatusBadge } from "./components/TotemStatusBadge";
import { TotemMovementHistory } from "./components/TotemMovementHistory";
import { useTotemDetail } from "./hooks/useTotemDetail";
import { useUpdateTotemStatus } from "./hooks/useTotems";
import { formatTotemNumber } from "./utils/format";
import { useProfileStore } from "~/store/profileStore";
import {
  ArrowLeft,
  Package,
  Calendar,
  RotateCw,
  MapPin,
  Truck,
  Warehouse,
  AlertTriangle,
} from "lucide-react";
import dayjs from "dayjs";
import "dayjs/locale/es";

dayjs.locale("es");

export default function TotemDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { id: userId } = useProfileStore();
  const { data: totem, isLoading } = useTotemDetail(id);
  const updateStatus = useUpdateTotemStatus();
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);

  const isReturning = updateStatus.isPending;

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!totem) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={() => navigate("/totems")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver
        </Button>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Totem no encontrado</p>
        </div>
      </div>
    );
  }

  const handleMarkEmpty = async () => {
    if (!userId) return;
    await updateStatus.mutateAsync({
      id: totem.id,
      status: "pickup_ready",
      performedBy: userId,
      options: { notes: "Marcado como vacío" },
    });
  };

  const returnDestinations = {
    bodega: { status: "returned" as const, notes: "Retornado a bodega" },
    transito: { status: "in_transit" as const, notes: "Enviado a uso interno" },
  };

  const handleReturnTo = async (
    destination: keyof typeof returnDestinations
  ) => {
    if (!userId) return;
    const { status, notes } = returnDestinations[destination];
    await updateStatus.mutateAsync({
      id: totem.id,
      status,
      performedBy: userId,
      options: { notes },
    });
    setReturnDialogOpen(false);
  };

  const handleMarkDamaged = async () => {
    if (!userId) return;
    await updateStatus.mutateAsync({
      id: totem.id,
      status: "damaged",
      performedBy: userId,
      options: { notes: "Marcado como dañado" },
    });
  };

  const handleCompleteCleaning = async () => {
    if (!userId) return;
    await updateStatus.mutateAsync({
      id: totem.id,
      status: "available",
      performedBy: userId,
      options: { notes: "Limpieza completada" },
    });
  };

  const getDaysInStatus = () => {
    return dayjs().diff(dayjs(totem.updated_at), "day");
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate("/totems")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver
          </Button>
          <div className="flex items-center gap-3">
            <Package className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-2xl font-bold">
                {formatTotemNumber(totem.code)}
              </h1>
              <p className="text-muted-foreground">
                Capacidad: {totem.capacity_liters}L
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* General Info */}
          <Card>
            <CardHeader>
              <CardTitle>Información General</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Número</p>
                <p className="font-medium">{formatTotemNumber(totem.code)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Capacidad</p>
                <p className="font-medium">{totem.capacity_liters} Litros</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Ciclos Completados</p>
                <p className="font-medium">{totem.total_cycles}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Fecha de Alta</p>
                <p className="font-medium">
                  {totem.acquired_at
                    ? dayjs(totem.acquired_at).format("DD MMM YYYY")
                    : "-"}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Última Limpieza</p>
                <p className="font-medium">
                  {totem.last_cleaned_at
                    ? dayjs(totem.last_cleaned_at).format("DD MMM YYYY")
                    : "Nunca"}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Última Actualización</p>
                <p className="font-medium">
                  {dayjs(totem.updated_at).format("DD MMM YYYY, HH:mm")}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Current Usage */}
          {(totem.client || totem.product) && (
            <Card>
              <CardHeader>
                <CardTitle>Uso Actual</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {totem.client && (
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Cliente:</span>
                    <span className="font-medium">{totem.client.name}</span>
                  </div>
                )}
                {totem.product && (
                  <div className="flex items-center gap-2">
                    <Package className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Producto:</span>
                    <span className="font-medium">{totem.product.short_name}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Movement History */}
          <TotemMovementHistory totemId={totem.id} />
        </div>

        {/* Right column - Status & Actions */}
        <div className="space-y-6">
          {/* Current Status */}
          <Card>
            <CardHeader>
              <CardTitle>Estado Actual</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-center py-4">
                <TotemStatusBadge status={totem.status} />
              </div>

              {totem.client && (
                <div className="text-center text-sm text-muted-foreground">
                  <MapPin className="h-4 w-4 inline mr-1" />
                  {totem.client.name}
                </div>
              )}

              <div className="text-center text-sm text-muted-foreground">
                <Calendar className="h-4 w-4 inline mr-1" />
                {getDaysInStatus()} días en este estado
              </div>

              {/* Action Buttons based on status */}
              <div className="space-y-2 pt-4">
                {totem.status === "at_client" && (
                  <Button
                    className="w-full"
                    onClick={handleMarkEmpty}
                    disabled={updateStatus.isPending}
                  >
                    <Package className="h-4 w-4 mr-2" />
                    Marcar como Vacío
                  </Button>
                )}

                {(totem.status === "pickup_ready" ||
                  totem.status === "pickup_scheduled") && (
                  <Button
                    className="w-full"
                    onClick={() => setReturnDialogOpen(true)}
                    disabled={isReturning}
                  >
                    <Truck className="h-4 w-4 mr-2" />
                    Retornar Tótem
                  </Button>
                )}

                {totem.status === "cleaning" && (
                  <Button
                    className="w-full"
                    onClick={handleCompleteCleaning}
                    disabled={updateStatus.isPending}
                  >
                    <RotateCw className="h-4 w-4 mr-2" />
                    Completar Limpieza
                  </Button>
                )}

                {!["damaged", "lost"].includes(totem.status) && (
                  <Button
                    variant="outline"
                    className="w-full text-red-600 hover:text-red-700"
                    onClick={handleMarkDamaged}
                    disabled={updateStatus.isPending}
                  >
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    Reportar Daño
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Return destination chooser */}
      <Dialog open={returnDialogOpen} onOpenChange={setReturnDialogOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Retornar Tótem</DialogTitle>
            <DialogDescription>¿A dónde retorna el tótem?</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Button
              variant="outline"
              className="h-auto w-full justify-start gap-3 py-3"
              onClick={() => handleReturnTo("bodega")}
              disabled={isReturning}
            >
              <Warehouse className="h-5 w-5 shrink-0 text-muted-foreground" />
              <div className="text-left">
                <div className="font-medium">Bodega</div>
                <div className="text-xs text-muted-foreground">
                  Guardar en bodega (Retornado)
                </div>
              </div>
            </Button>
            <Button
              variant="outline"
              className="h-auto w-full justify-start gap-3 py-3"
              onClick={() => handleReturnTo("transito")}
              disabled={isReturning}
            >
              <Truck className="h-5 w-5 shrink-0 text-muted-foreground" />
              <div className="text-left">
                <div className="font-medium">Uso Interno</div>
                <div className="text-xs text-muted-foreground">
                  Enviar a uso interno
                </div>
              </div>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
